import { Component, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { filter, map, startWith } from 'rxjs/operators';
import { Crop, CropVarietySpecificNormValue, GrasslandUsage, LocationInCountry, TotalNitrogenLegalNorm } from 'src/app/shared/models';

import { TotalNitrogenLegalNormDialogModel } from './total-nitrogen-legal-norm-dialog.model';
import { DisplayService } from '../../../shared/services/display.service';

@Component({
  selector: 'app-total-nitrogen-legal-norm-dialog',
  templateUrl: './total-nitrogen-legal-norm-dialog.component.html',
  standalone: false,
})
export class TotalNitrogenLegalNormDialogComponent extends AbstractFormDialog<TotalNitrogenLegalNorm> {
  data = inject<TotalNitrogenLegalNormDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<TotalNitrogenLegalNormDialogComponent, AbstractDialogResultData<TotalNitrogenLegalNorm>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  displayService = inject(DisplayService);

  formGroup = this.fb.group({
    id: [this.data.totalNitrogenLegalNorm.id],
    calendarYear: [this.data.totalNitrogenLegalNorm.calendarYear, [Validators.required]],
    cropId: [this.data.totalNitrogenLegalNorm.cropId, [Validators.required]],
    soilTypeId: [this.data.totalNitrogenLegalNorm.soilTypeId, [Validators.required]],
    location: [this.data.totalNitrogenLegalNorm.location],
    isPreviousCropCorn: [this.data.totalNitrogenLegalNorm.isPreviousCropCorn],
    isPreviousCropNotGrain: [this.data.totalNitrogenLegalNorm.isPreviousCropNotGrain],
    grasslandUsage: [this.data.totalNitrogenLegalNorm.grasslandUsage],
    shouldSplitNorm: [this.data.totalNitrogenLegalNorm.shouldSplitNorm],
    cropVarietySpecificNorm: [this.data.totalNitrogenLegalNorm.cropVarietySpecificNorm],
    startDateBefore: [this.data.totalNitrogenLegalNorm.startDateBefore],
    startDateAfter: [this.data.totalNitrogenLegalNorm.startDateAfter],
    endDateBefore: [this.data.totalNitrogenLegalNorm.endDateBefore],
    endDateAfter: [this.data.totalNitrogenLegalNorm.endDateAfter],
    startDatePreviousYearBefore: [this.data.totalNitrogenLegalNorm.startDatePreviousYearBefore],
    startDatePreviousYearAfter: [this.data.totalNitrogenLegalNorm.startDatePreviousYearAfter],
    isNitrogenDifferentiation: [this.data.totalNitrogenLegalNorm.isNitrogenDifferentiation],
    isAfterTornGrass: [this.data.totalNitrogenLegalNorm.isAfterTornGrass],
    nitrogenNorm: [this.data.totalNitrogenLegalNorm.nitrogenNorm, [Validators.required]],
    isGroundWaterPreservationWithNitrogenCuts: [this.data.totalNitrogenLegalNorm.isGroundWaterPreservationWithNitrogenCuts],
    isNutrientsPollutedArea: [this.data.totalNitrogenLegalNorm.isNutrientsPollutedArea],
  });

  locations = LocationInCountry;
  grasslandUsages = GrasslandUsage;
  cropVarietySpecificNormValues = CropVarietySpecificNormValue;

  filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.removeDuplicateCrops([...this.filter(value, [...this.data.crops])])),
  );

  isNewTotalNitrogenLegalNorm() {
    return !this.data.totalNitrogenLegalNorm.id;
  }

  confirm() {
    super.confirm({
      ...this.data.totalNitrogenLegalNorm,
      ...this.formGroup.getRawValue(),
      agrifirmCropId: this.getCrop(this.formGroup.get('cropId').value)?.agrifirmCropId,
      cropName: this.getCrop(this.formGroup.get('cropId').value)?.name,
    });
  }

  getCrop = (cropId?: number) => (cropId ? this.data.crops.find((crop) => crop.id === cropId) : null);

  displayCrop() {
    return (cropId?: number) => this.getCrop(cropId)?.name ?? '';
  }

  getLocationString = (key: string): string => this.displayService.getLocationString(key);

  getGrasslandUsageString = (key: string): string => this.displayService.getGrasslandUsageString(key);

  getCropVarietySpecificNormString = (key: string): string => this.displayService.getCropVarietySpecificNormString(key);

  private filter(value: string, items: Crop[]): Crop[];
  private filter(value: string, items: Crop[]) {
    return [...items]
      .sort((a, b) => {
        if (!(a.name && b.name)) {
          return 0;
        }
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      })
      .filter((option) => {
        if (!option.name) {
          return false;
        }
        return option.name.toLowerCase().includes(value.toLowerCase());
      });
  }

  private removeDuplicateCrops(items: Crop[]): Crop[] {
    return items.filter((v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i);
  }
}
